import isEqual from "lodash.isequal";
import React, { useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core";
import { HvUiKitThemeNames } from "@hitachivantara/uikit-react-core";
import { ThemeContext } from "lib/context/ThemeContext";
import { MapBound } from "lib/api";
import { UserPolicyEntity } from "lib/api/userSettings";
import {
  PreventNavigationContext,
  NavigationContext,
} from "./NavigationContext";
import { MapContext } from "./MapContext";

const useStyles = makeStyles(() => ({
  container: {
    width: "100%",
    overflowX: "hidden",
    overflowY: "auto",
  },
}));

export const EmbeddedContext = React.createContext(null);

export interface EmbeddedAppProps extends QiankunProps {
  mapBound?: MapBound;
  typeIds?: string[];
  refreshTimestamp?: number;
  workflowDisabled?: boolean;
  userOpExists?: boolean;
  enableCustomDashboard?: boolean;
  keycloak?: Keycloak.KeycloakInstance;
  userOperations?: UserPolicyEntity[];
}

export const EmbeddedProvider: React.FC<EmbeddedAppProps> = ({
  setGlobalState,
  onGlobalStateChange,
  children,
  mapBound,
  typeIds,
  refreshTimestamp = 0,
  workflowDisabled = false,
  userOpExists = false,
  enableCustomDashboard = false,
  keycloak,
}) => {
  window.KEYCLOAK = keycloak;

  const { theme, setTheme } = useContext(ThemeContext);
  const { i18n } = useTranslation();
  const classes = useStyles();

  const { preventNavigation, askNavigateTo } = useContext(
    PreventNavigationContext
  );

  const {
    setRefreshTimestamp,
    setTypeIds,
    mapBound: contextMapBound,
    setMapBound,
  } = useContext(MapContext);
  const { setWorkflowDisabled, setEnableCustomDashboard, setUserOpExists } =
    useContext(NavigationContext);

  useEffect(() => {
    if (mapBound?.northEast && mapBound?.southWest) {
      setMapBound({
        northEast: mapBound.northEast,
        southWest: mapBound.southWest,
      });
    }
  }, [mapBound, setMapBound]);

  useEffect(() => {
    setEnableCustomDashboard(enableCustomDashboard);
  }, [enableCustomDashboard, setEnableCustomDashboard]);

  useEffect(() => {
    setWorkflowDisabled(workflowDisabled);
  }, [workflowDisabled, setWorkflowDisabled]);

  useEffect(() => {
    setUserOpExists(userOpExists);
  }, [userOpExists, setUserOpExists]);

  useEffect(() => {
    if (typeIds) {
      setTypeIds(typeIds);
    }
  }, [typeIds, setTypeIds]);

  useEffect(() => {
    if (refreshTimestamp) {
      setRefreshTimestamp(refreshTimestamp);
    }
  }, [refreshTimestamp, setRefreshTimestamp]);

  useEffect(() => {
    onGlobalStateChange?.((state, prevState) => {
      const hasThemeChanged = state.theme !== prevState.theme;
      if (hasThemeChanged) setTheme(state.theme as HvUiKitThemeNames);

      const hasLanguageChanged = state.language !== prevState.language;
      if (hasLanguageChanged) i18n.changeLanguage(state.language as string);

      const hasTimestampChanged =
        state.refreshTimestamp !== prevState.refreshTimestamp;
      if (hasTimestampChanged)
        setRefreshTimestamp(state.refreshTimestamp as number);

      const hasMapBoundChanged = !isEqual(state.mapBound, prevState.mapBound);
      if (hasMapBoundChanged) setMapBound(state.mapBound as MapBound);
      else if (contextMapBound !== prevState.mapBound)
        // this happens in the first render, because the map bound is not available
        setMapBound(state.mapBound as MapBound);

      const hasVisibleIncidentsChanged = !isEqual(
        state.typeIds,
        prevState.typeIds
      );
      if (hasVisibleIncidentsChanged) setTypeIds(state.typeIds as string[]);
    });
  }, [
    onGlobalStateChange,
    setTheme,
    i18n,
    setMapBound,
    setTypeIds,
    setRefreshTimestamp,
    contextMapBound,
  ]);

  useEffect(() => {
    setGlobalState?.({ theme });
  }, [setGlobalState, theme]);

  useEffect(() => {
    setGlobalState?.({ askNavigateTo });
  }, [setGlobalState, askNavigateTo]);

  useEffect(() => {
    setGlobalState?.({ preventNavigation });
  }, [setGlobalState, preventNavigation]);

  return (
    <EmbeddedContext.Provider value={null}>
      <div id="wis-container" className={classes.container}>
        {children}
      </div>
    </EmbeddedContext.Provider>
  );
};
